/**
 * The route: one row per operation, in sequence, with the machine it runs on and a bar
 * showing WHEN it runs.
 *
 * The bar is scaled to the works order's OWN span, not to the 13-week window the reader
 * came from. The question here is "when does this job happen", and a job whose legs all sit
 * outside the window would otherwise draw thirteen empty weeks and nothing else. A marker
 * on the current week keeps the two frames related without letting one clip the other.
 *
 * Colour is by POSITION relative to that window — late, inside it, past the horizon —
 * because that is the fact a planner reads a route for. The row the reader arrived from is
 * accented, so "where am I in this job" never needs working out.
 */

import React from 'react'
import type { WorksOrderOp } from '../../types/worksOrder'
import { hrsFine } from './format'

const DAY = 86400000
const at = (iso: string | null): number => (iso ? new Date(`${iso}T00:00:00`).getTime() : NaN)

const day = (iso: string | null): string =>
  iso === null ? '—' : new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })

const TONE: Record<WorksOrderOp['position'], string> = {
  overdue: 'var(--lc-red)',
  in_window: 'var(--lc-teal)',
  beyond: 'var(--lc-mut)',
}

const ROW = 'grid grid-cols-[38px_1fr_92px_54px_104px_minmax(120px,1.1fr)] items-center gap-x-2.5 border-t py-1.5'

/** The op's slice of the works order's span, as left/width percentages. A same-day
 *  operation still gets a visible sliver — a bar of zero width reads as missing data. */
function bar(op: WorksOrderOp, from: number, to: number): { left: string; width: string } {
  const span = Math.max(to - from, DAY)
  const start = Number.isNaN(at(op.op_start_date)) ? from : at(op.op_start_date)
  const finish = Number.isNaN(at(op.op_finish_date)) ? start : at(op.op_finish_date)
  return {
    left: `${Math.max(0, ((start - from) / span) * 100)}%`,
    width: `${Math.max(2.5, (((finish - start) + DAY) / span) * 100)}%`,
  }
}

function Row({ op, from, to, here }: {
  op: WorksOrderOp
  from: number
  to: number
  here: boolean
}): React.JSX.Element {
  const geometry = bar(op, from, to)
  return (
    <div
      className={ROW}
      style={{ borderColor: 'var(--lc-b07)', background: here ? 'var(--lc-blue-soft)' : undefined }}
    >
      <span className="font-mono text-[11px] font-semibold" style={{ color: 'var(--lc-ink)' }}>{op.op_number}</span>
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-sans text-[11.5px]" style={{ color: 'var(--lc-ink)' }}>
          {op.resource_name}
        </span>
        <span className="font-mono text-[10px]" style={{ color: 'var(--lc-faint)' }}>{op.resource_code}</span>
      </span>
      <span
        className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10px]"
        title={op.is_cnc ? undefined : 'no agreed capacity basis — hours only, in none of the load ratios'}
        style={{ color: op.is_cnc ? 'var(--lc-ink4)' : 'var(--lc-faint)' }}
      >
        {op.is_cnc ? op.resource_group : `${op.resource_group} ·`}
      </span>
      <span className="text-right font-mono text-[11px] font-semibold tabular-nums" style={{ color: 'var(--lc-ink)' }}>
        {op.is_untimed ? '—' : hrsFine(op.load_hours)}
      </span>
      <span className="font-mono text-[10px] tabular-nums" style={{ color: 'var(--lc-mut)' }}>
        {day(op.op_start_date)} → {day(op.op_finish_date)}
      </span>
      <span className="relative h-[9px] rounded-[2px]" style={{ background: 'var(--lc-b05)' }}>
        <span className="absolute inset-y-0 rounded-[2px]" style={{ ...geometry, background: TONE[op.position] }} />
      </span>
    </div>
  )
}

export default function WorksOrderRoute({ ops, group, from, to, now }: {
  ops: WorksOrderOp[]
  /** The resource group the reader arrived from — its legs are accented. */
  group: string
  from: string | null
  to: string | null
  /** Monday of the current week, so the span can say where "now" falls across it. */
  now: string
}): React.JSX.Element {
  const start = at(from)
  const end = at(to)
  const nowLeft = ((at(now) - start) / Math.max(end - start, DAY)) * 100
  return (
    <div className="px-4 pb-1">
      <div className="grid grid-cols-[38px_1fr_92px_54px_104px_minmax(120px,1.1fr)] gap-x-2.5 pb-1 font-mono text-[8.5px] font-semibold uppercase tracking-[.08em]" style={{ color: 'var(--lc-mut)' }}>
        <div>Op</div><div>Machine</div><div>Group</div><div className="text-right">Hours</div><div>Runs</div>
        <div className="relative">
          When
          {nowLeft >= 0 && nowLeft <= 100 && (
            <span className="absolute -bottom-1 top-2 w-px" style={{ left: `${nowLeft}%`, background: 'var(--lc-blue)' }} />
          )}
        </div>
      </div>
      {ops.map((op) => (
        <Row key={`${op.op_number}-${op.resource_code}`} op={op} from={start} to={end} here={op.resource_group === group} />
      ))}
    </div>
  )
}
